import { assignPublicAbVariant, trackPublicAbEvent } from "./consultationApiClient";
import { getSessionId } from "./posthog";

const VISITOR_ID_KEY = "astroji_landing_ab_visitor_id";
const LOCAL_ASSIGNMENT_COUNTER_KEY = "astroji_landing_ab_local_counter";

export const CONSULTATION_LANDING_EXPERIMENT_KEY = "consultation_landing_pages";

const CONSULTATION_LANDING_LOCAL_VARIANTS = Array.from({ length: 11 }, (_, index) => {
  const page = index + 1;
  return {
    variant_id: `cons_${String(page).padStart(2, "0")}`,
    path: `/lp/cons/${page}`,
  };
});

export type LandingAbAssignment = {
  success: boolean;
  experiment_key: string;
  assignment_id: string | null;
  variant_id: string;
  path: string;
  sticky: boolean;
  persisted: boolean;
  warning?: string;
  request_id?: string;
};

type TrackEventInput = {
  event: string;
  assignmentId?: string | null;
  variantId?: string | null;
  metadata?: Record<string, unknown>;
};

const safeStorageGet = (storage: Storage, key: string) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (storage: Storage, key: string, value: string) => {
  try {
    storage.setItem(key, value);
  } catch {
    // A/B assignment should never block page rendering.
  }
};

const createClientId = () => {
  if (window.crypto?.randomUUID) {
    return `lpv_${window.crypto.randomUUID()}`;
  }
  return `lpv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
};

export function getLandingAbVisitorId() {
  const existing = safeStorageGet(localStorage, VISITOR_ID_KEY);
  if (existing) return existing;

  const next = createClientId();
  safeStorageSet(localStorage, VISITOR_ID_KEY, next);
  return next;
}

function getCurrentAbSearchContext() {
  const search = new URLSearchParams(window.location.search);
  return {
    experimentKey: search.get("ab_experiment") || CONSULTATION_LANDING_EXPERIMENT_KEY,
    assignmentId: search.get("ab_assignment"),
    variantId: search.get("ab_variant"),
    forceFresh:
      search.get("ab_fresh") === "1" ||
      search.get("ab_force") === "1" ||
      search.get("ab_reset") === "1",
    localPreview:
      search.get("ab_preview") === "1" ||
      search.get("ab_local") === "1",
  };
}

function getPagePayload(metadata: Record<string, unknown> = {}) {
  return {
    currentPath: window.location.pathname,
    pageUrl: window.location.href,
    referrer: document.referrer || "",
    visitorId: getLandingAbVisitorId(),
    sessionId: getSessionId(),
    metadata: {
      source: "astroji_frontend",
      ...metadata,
    },
  };
}

function getLocalDevelopmentAssignment(warning: string) {
  const counter = Number(
    safeStorageGet(localStorage, LOCAL_ASSIGNMENT_COUNTER_KEY) || 0,
  );
  const variant =
    CONSULTATION_LANDING_LOCAL_VARIANTS[
      counter % CONSULTATION_LANDING_LOCAL_VARIANTS.length
    ];
  safeStorageSet(
    localStorage,
    LOCAL_ASSIGNMENT_COUNTER_KEY,
    String(counter + 1),
  );

  return {
    success: true,
    experiment_key: CONSULTATION_LANDING_EXPERIMENT_KEY,
    assignment_id: `local-${counter + 1}`,
    variant_id: variant.variant_id,
    path: variant.path,
    sticky: false,
    persisted: false,
    warning,
  } satisfies LandingAbAssignment;
}

export async function assignConsultationLandingVariant(
  metadata: Record<string, unknown> = {},
) {
  const context = getCurrentAbSearchContext();

  if (process.env.NODE_ENV === 'development' && context.localPreview) {
    return getLocalDevelopmentAssignment("Using local development A/B preview.");
  }

  try {
    const data = await assignPublicAbVariant({
      experimentKey: CONSULTATION_LANDING_EXPERIMENT_KEY,
      forceNewAssignment: context.forceFresh,
      ...getPagePayload(metadata),
    });

    if (!data?.success || !data.path) {
      return getLocalDevelopmentAssignment(
        data?.warning || "Using local A/B fallback.",
      );
    }

    return data;
  } catch (err: unknown) {
    return getLocalDevelopmentAssignment(
      err instanceof Error ? err.message : "Using local A/B fallback.",
    );
  }
}

export async function trackLandingAbEvent(input: TrackEventInput) {
  const context = getCurrentAbSearchContext();
  const assignmentId = input.assignmentId ?? context.assignmentId;
  const variantId = input.variantId ?? context.variantId;

  if (!assignmentId && !variantId) {
    return null;
  }

  try {
    const data = await trackPublicAbEvent({
      experimentKey: context.experimentKey,
      assignmentId,
      variantId,
      event: input.event,
      ...getPagePayload(input.metadata),
    });
    return data;
  } catch (err: unknown) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("[Landing A/B] event tracking failed:", err instanceof Error ? err.message : err);
    }
    return null;
  }
}
